import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { t, useLang } from "../lib/i18n";

/**
 * /cancel-plan — landing page for the "Cancel subscription" CTA in the
 * pre-renewal reminder emails. Two-step flow so we can capture why a
 * subscriber is leaving before actually cancelling:
 *
 *   1. Plan summary + red "Continue to cancel" button.
 *   2. Reason picker (required chip) + optional free-text.
 *      Submitting POSTs {reason, reason_details} to /me/unsubscribe.
 *   3. Success state showing the retained-until date.
 *
 * Wrapped in ProtectedRoute so the email link forces login before
 * anything can be cancelled.
 */

const REASONS = [
  { key: "too_expensive", label: "Too expensive" },
  { key: "not_using_enough", label: "Not using it enough" },
  { key: "missing_feature", label: "Missing a feature" },
  { key: "found_alternative", label: "Found an alternative" },
  { key: "taking_break", label: "Just taking a break" },
  { key: "technical_issues", label: "Technical issues" },
  { key: "other", label: "Other" },
];

export default function CancelPlanPage() {
  useLang();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [done, setDone] = useState(false);
  const [step, setStep] = useState("intro"); // "intro" | "reason"
  const [reason, setReason] = useState("");
  const [reasonDetails, setReasonDetails] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/me/subscription").then(({ data }) => {
      if (mounted) setSubscription(data);
    }).catch(() => {}).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const doCancel = async () => {
    if (!reason) {
      toast.error("Please pick a reason so we can improve.");
      return;
    }
    if (reason === "other" && reasonDetails.trim().length < 3) {
      toast.error("Please add a short note so we know what went wrong.");
      return;
    }
    setCancelling(true);
    try {
      const body = { reason, reason_details: reasonDetails.trim() };
      const { data } = await api.post("/me/unsubscribe", body);
      toast.success(data.message || "Reads+ will end at the period.");
      setDone(true);
      const { data: fresh } = await api.get("/me/subscription");
      setSubscription(fresh);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] bg-[#F2EAD3] flex items-center justify-center" data-testid="cancel-plan-loading">
        <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252]">Loading…</div>
      </div>
    );
  }

  const isPlus = subscription?.is_plus;
  const alreadyCancelling = subscription?.auto_renew === false || subscription?.cancel_requested;
  const expiresLabel = subscription?.expires_at
    ? new Date(subscription.expires_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  return (
    <div className="min-h-[80vh] bg-[#F2EAD3] py-16 px-4" data-testid="cancel-plan-page">
      <div className="max-w-xl mx-auto bg-[#FAF5E6] border border-[rgba(10,10,10,0.10)] p-8 sm:p-10">
        <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#525252] inline-flex items-center gap-1.5 mb-3">
          <BadgeCheck className="w-3 h-3" strokeWidth={1.5} /> Reads+
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#0A0A0A] tracking-tight mb-4">
          {done
            ? t("account.cancel_title")
            : step === "reason"
              ? "One quick question before you go"
              : "Cancel your Reads+ subscription?"}
        </h1>

        {!isPlus && (
          <div data-testid="cancel-plan-not-plus">
            <p className="text-sm text-[#4A4030] mb-6">
              You're not on Reads+ right now — there's nothing to cancel. If you upgrade
              later, you can always cancel from this page or your account settings.
            </p>
            <Link
              to="/account"
              className="inline-block bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors"
              data-testid="cancel-plan-back-to-account"
            >
              Back to account
            </Link>
          </div>
        )}

        {isPlus && alreadyCancelling && (
          <div data-testid="cancel-plan-already-cancelled">
            <p className="text-sm text-[#4A4030] mb-6">
              You've already cancelled auto-renewal. Reads+ stays active until{" "}
              <strong>{expiresLabel}</strong>. After that your account moves back to the free plan —
              your books and reviews stay put.
            </p>
            <Link
              to="/account"
              className="inline-block bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors"
              data-testid="cancel-plan-back-to-account"
            >
              Back to account
            </Link>
          </div>
        )}

        {isPlus && !alreadyCancelling && !done && step === "intro" && (
          <div data-testid="cancel-plan-form">
            <p className="text-sm text-[#4A4030] mb-5">
              {t("account.cancel_body_a")}{" "}
              <strong>{expiresLabel || "the end of your current period"}</strong>. {t("account.cancel_body_b")}
            </p>
            <ul className="text-sm text-[#4A4030] mb-6 space-y-1.5 list-disc pl-5">
              <li>You keep every Reads+ perk until the renewal date.</li>
              <li>No further charges will be attempted.</li>
              <li>Your library, reviews, and lists are never deleted.</li>
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setStep("reason")}
                className="bg-[#8A0F0F] hover:bg-[#5C0808] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors"
                data-testid="cancel-plan-continue"
              >
                Continue to cancel
              </button>
              <button
                type="button"
                onClick={() => navigate("/account")}
                className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A]"
                data-testid="cancel-plan-keep"
              >
                {t("account.keep_plus")}
              </button>
            </div>
          </div>
        )}

        {isPlus && !alreadyCancelling && !done && step === "reason" && (
          <div data-testid="cancel-plan-reason">
            <p className="text-sm text-[#4A4030] mb-5">
              What's the main reason you're cancelling? This is anonymous inside the
              team — it just helps us fix whatever's not working.
            </p>
            <div className="flex flex-wrap gap-2 mb-5" role="radiogroup" aria-label="Cancel reason">
              {REASONS.map((r) => {
                const selected = r.key === reason;
                return (
                  <button
                    key={r.key}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setReason(r.key)}
                    disabled={cancelling}
                    data-testid={`cancel-reason-${r.key}`}
                    className={
                      "px-4 py-2 text-xs font-mono uppercase tracking-widest border transition-colors " +
                      (selected
                        ? "bg-[#0A0A0A] text-[#F2EAD3] border-[#0A0A0A]"
                        : "bg-transparent text-[#4A4030] border-[rgba(10,10,10,0.20)] hover:border-[#0A0A0A] hover:text-[#0A0A0A]")
                    }
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
            {reason === "other" && (
              <textarea
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value.slice(0, 1000))}
                placeholder="Tell us what happened — the more specific the better."
                rows={4}
                maxLength={1000}
                disabled={cancelling}
                data-testid="cancel-reason-other-details"
                className="w-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.20)] px-3 py-2 text-sm text-[#0A0A0A] placeholder:text-[#8A8070] focus:outline-none focus:border-[#0A0A0A] mb-5"
              />
            )}
            {reason && reason !== "other" && (
              <textarea
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value.slice(0, 1000))}
                placeholder="Anything else you want to add? (optional)"
                rows={3}
                maxLength={1000}
                disabled={cancelling}
                data-testid="cancel-reason-optional-details"
                className="w-full bg-[#F2EAD3] border border-[rgba(10,10,10,0.20)] px-3 py-2 text-sm text-[#0A0A0A] placeholder:text-[#8A8070] focus:outline-none focus:border-[#0A0A0A] mb-5"
              />
            )}
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={doCancel}
                disabled={cancelling || !reason}
                className="bg-[#8A0F0F] hover:bg-[#5C0808] disabled:opacity-40 disabled:cursor-not-allowed text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors"
                data-testid="cancel-plan-confirm"
              >
                {cancelling ? t("account.cancelling") : "Confirm cancel"}
              </button>
              <button
                type="button"
                onClick={() => { setStep("intro"); setReason(""); setReasonDetails(""); }}
                disabled={cancelling}
                className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] hover:text-[#0A0A0A]"
                data-testid="cancel-plan-back"
              >
                Back
              </button>
            </div>
          </div>
        )}

        {done && (
          <div data-testid="cancel-plan-done">
            <p className="text-sm text-[#4A4030] mb-6">
              You'll keep Reads+ until <strong>{expiresLabel}</strong>. After that your account
              moves back to the free plan — your books and reviews stay put. We won't try to
              charge you again.
            </p>
            <p className="text-sm text-[#4A4030] mb-6">
              Thanks for the feedback — a human at Reads reads every response.
            </p>
            <Link
              to="/account"
              className="inline-block bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm transition-colors"
              data-testid="cancel-plan-back-to-account"
            >
              Back to account
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
